import { useAuth } from "@/src/contexts/auth.context";
import { Season, Transaction, UserStarBalance } from "@/src/models/rewardly.model";
import {
  useGetAllSeasons,
  useGetSeasonStarBalancesInfinite,
  useGetSeasonStarTransactions,
} from "@/src/services/api/rewardly/rewardly.hooks";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Toast from "react-native-toast-message";
import * as XLSX from 'xlsx';
import { formatReward } from "./use-my-stars";

export const BALANCES_PAGE_SIZE = 10;

const useBalances = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const { orgId } = useAuth();
  const { data: seasonsData } = useGetAllSeasons(orgId as string);
  const seasons: Season[] = seasonsData?.getseasons?.items ?? [];
  const latestSeason = seasons[0];
  const [selectedSeasonId, setSelectedSeasonId] = useState<string | null>(null);
  const resolvedSeasonId = selectedSeasonId ?? latestSeason?.ItemId ?? null;
  const selectedSeason = seasons.find(s => s.ItemId === resolvedSeasonId);
  const isCurrentSeason = selectedSeason?.status === "active";

  const [isExporting, setIsExporting] = useState(false);

  const seasonOptions = seasons.map(s => ({ label: s.name, value: s.ItemId }));

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useGetSeasonStarBalancesInfinite(resolvedSeasonId, BALANCES_PAGE_SIZE);

  // Load every page so search and pagination cover the whole season, not just
  // the first page (otherwise searching a user beyond page 1 finds nothing).
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const { data: seasonTransactionsData } = useGetSeasonStarTransactions(resolvedSeasonId)

  const balancesWithCalculatedStars = useMemo(() => {
    const balances: UserStarBalance[] = data?.pages.flatMap(
      (page: any) => page?.getuserStarBalances?.items ?? []
    ) ?? [];
    const seasonTransactions: Transaction[] =
      seasonTransactionsData?.getstarTransactions?.items ?? [];

    return balances.map(balance => {
      const starsReceived = seasonTransactions
        .filter(t => t.recipientId === balance.userId)
        .reduce((sum, t) => sum + (t.amount ?? 0), 0);

      return {
        ...balance,
        starsReceived,
        monetaryValue: formatReward(starsReceived, selectedSeason?.monetaryValue),
      };
    });
  }, [data, seasonTransactionsData, selectedSeason?.monetaryValue]);

  const filteredBalances = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return balancesWithCalculatedStars;
    return balancesWithCalculatedStars.filter(b =>
      (b.userInfo?.displayName ?? "").toLowerCase().includes(query) ||
      (b.userInfo?.email ?? "").toLowerCase().includes(query),
    );
  }, [balancesWithCalculatedStars, search]);

  const totalItems = filteredBalances.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / BALANCES_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedBalances = filteredBalances.slice(
    (currentPage - 1) * BALANCES_PAGE_SIZE,
    currentPage * BALANCES_PAGE_SIZE,
  );

  const handleSearch = (text: string) => {
    setSearch(text);
    setPage(1);
  };
  const handleSeasonChange = (value: string) => {
    setSelectedSeasonId(value);
    setPage(1);
  };

  const handleExport = () => {
    setIsExporting(true);
    try {
      const rows = filteredBalances.map(b => ({
        "Name": b.userInfo?.displayName ?? "",
        "Email": b.userInfo?.email ?? "",
        "Stars Received": b.starsReceived ?? 0,
        "Monetary Value": b.monetaryValue,
        "Allowance Remaining": b.starsRemaining ?? 0,
      }));

      const ws = XLSX.utils.json_to_sheet(rows);

      // column widths
      ws['!cols'] = [
        { wch: 25 },
        { wch: 30 },
        { wch: 18 },
        { wch: 20 },
        { wch: 20 },
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Balances");
      XLSX.writeFile(wb, `season-${seasonOptions.find(s => s.value === resolvedSeasonId)?.label ?? resolvedSeasonId}-balances.xlsx`);
    } catch {
      Toast.show({ type: "error", text1: t("ERROR_EXPORT_FAILED") });
    } finally {
      setIsExporting(false);
    }
  };

  return {
    search, handleSearch,
    selectedSeasonId: resolvedSeasonId,
    handleSeasonChange,
    seasonOptions,
    isCurrentSeason,
    balances: paginatedBalances,
    totalItems,
    totalPages,
    currentPage,
    setPage,
    isLoading,
    handleExport,
    isExporting
  };
};

export default useBalances;
